import { ref, computed } from 'vue';
import QuotesAPI from 'dashboard/api/planning/quotes';

const COMPARABLE_STATUSES = ['not_sent', 'sent', 'viewed', 'accepted'];

export function useEventQuotes(eventId) {
  const quotes = ref([]);
  const loading = ref(false);
  const selectedQuoteId = ref(null);
  const compareQuoteId = ref(null);
  const filter = ref('open'); // open | closed | all
  const showBuilder = ref(false);
  const builderSeedQuote = ref(null);
  const showSendDialog = ref(false);
  const sendQuoteTarget = ref(null);

  const eid = () =>
    typeof eventId === 'function' ? eventId() : eventId.value || eventId;

  const preferredQuote = list =>
    list.find(q => q.status === 'accepted') ||
    list.find(q => ['sent', 'viewed'].includes(q.status)) ||
    list.find(q => q.status === 'not_sent');

  const selectPreferredQuote = list => {
    const preferred = preferredQuote(list);
    selectedQuoteId.value = preferred?.id ?? null;
    compareQuoteId.value =
      preferred && COMPARABLE_STATUSES.includes(preferred.status)
        ? preferred.id
        : null;
  };

  const fetchQuotes = async () => {
    const id = eid();
    if (!id) return;
    const requestEventId = id;
    loading.value = true;
    quotes.value = [];
    selectedQuoteId.value = null;
    compareQuoteId.value = null;
    try {
      const res = await QuotesAPI.getAll(requestEventId);
      if (requestEventId !== eid()) return;
      quotes.value = res.data || [];
      selectPreferredQuote(quotes.value);
    } catch {
      if (requestEventId === eid()) quotes.value = [];
    } finally {
      if (requestEventId === eid()) loading.value = false;
    }
  };

  const refreshQuotes = async () => {
    const id = eid();
    if (!id) return;
    const requestEventId = id;
    try {
      const res = await QuotesAPI.getAll(requestEventId);
      if (requestEventId !== eid()) return;
      quotes.value = res.data || [];
      selectPreferredQuote(quotes.value);
    } catch {
      // Keep the quotes already on the sheet.
    }
  };

  const reset = () => {
    quotes.value = [];
    loading.value = false;
    selectedQuoteId.value = null;
    compareQuoteId.value = null;
    filter.value = 'open';
    showBuilder.value = false;
    builderSeedQuote.value = null;
    showSendDialog.value = false;
    sendQuoteTarget.value = null;
  };

  const filteredQuotes = computed(() => {
    if (filter.value === 'all') return quotes.value;
    if (filter.value === 'open') {
      return quotes.value.filter(q => COMPARABLE_STATUSES.includes(q.status));
    }
    return quotes.value.filter(q => !COMPARABLE_STATUSES.includes(q.status));
  });

  const comparableQuotes = computed(() =>
    quotes.value.filter(q => COMPARABLE_STATUSES.includes(q.status))
  );

  const hasIssuedQuotes = computed(() =>
    quotes.value.some(q => ['sent', 'viewed', 'accepted'].includes(q.status))
  );

  const planLocked = computed(() => quotes.value.length > 0);

  const compareQuote = computed(
    () => quotes.value.find(q => q.id === compareQuoteId.value) || null
  );

  const selectedQuote = computed(
    () => quotes.value.find(q => q.id === selectedQuoteId.value) || null
  );

  const openBuilder = (seed = null) => {
    builderSeedQuote.value = seed;
    showBuilder.value = true;
  };

  const closeBuilder = () => {
    showBuilder.value = false;
    builderSeedQuote.value = null;
  };

  const createQuote = async payload => {
    const res = await QuotesAPI.create(eid(), payload);
    await fetchQuotes();
    selectedQuoteId.value = res.data.id;
    return res.data;
  };

  const updateQuote = async (quoteId, payload) => {
    const res = await QuotesAPI.update(eid(), quoteId, payload);
    await fetchQuotes();
    selectedQuoteId.value = res.data.id;
    return res.data;
  };

  const reviseQuote = async (quoteId, payload) => {
    const res = await QuotesAPI.revise(eid(), quoteId, payload);
    await fetchQuotes();
    selectedQuoteId.value = res.data.id;
    return res.data;
  };

  const archiveQuote = async quoteId => {
    await QuotesAPI.archive(eid(), quoteId);
    if (selectedQuoteId.value === quoteId) selectedQuoteId.value = null;
    if (compareQuoteId.value === quoteId) compareQuoteId.value = null;
    await fetchQuotes();
  };

  const sendQuote = async quoteId => {
    sendQuoteTarget.value =
      quotes.value.find(q => q.id === quoteId) || { id: quoteId };
    showSendDialog.value = true;
  };

  const closeSendDialog = () => {
    showSendDialog.value = false;
    sendQuoteTarget.value = null;
  };

  const onQuoteSent = async () => {
    closeSendDialog();
    await fetchQuotes();
  };

  const withdrawQuote = async quoteId => {
    await QuotesAPI.withdraw(eid(), quoteId);
    await fetchQuotes();
  };

  const acceptQuote = async (quoteId, acceptanceReason) => {
    await QuotesAPI.accept(eid(), quoteId, {
      acceptance_reason: acceptanceReason,
    });
    await fetchQuotes();
  };

  const rejectQuote = async (quoteId, rejectionReason) => {
    await QuotesAPI.reject(eid(), quoteId, {
      rejection_reason: rejectionReason,
    });
    await fetchQuotes();
  };

  const setCompareQuote = id => {
    compareQuoteId.value = id;
    selectedQuoteId.value = id;
  };

  return {
    quotes,
    loading,
    filter,
    filteredQuotes,
    comparableQuotes,
    hasIssuedQuotes,
    planLocked,
    selectedQuoteId,
    selectedQuote,
    compareQuoteId,
    compareQuote,
    showBuilder,
    builderSeedQuote,
    showSendDialog,
    sendQuoteTarget,
    fetchQuotes,
    refreshQuotes,
    openBuilder,
    closeBuilder,
    createQuote,
    updateQuote,
    reviseQuote,
    archiveQuote,
    sendQuote,
    closeSendDialog,
    onQuoteSent,
    withdrawQuote,
    acceptQuote,
    rejectQuote,
    setCompareQuote,
    reset,
  };
}
